/**
 * Client-side rate limiter — prevents rapid automated API abuse.
 *
 * Limits:
 * - Workout saves: max 10 per hour
 * - Score recomputes: max 20 per hour
 * - Duel creation: max 5 per hour
 * - Profile updates: max 30 per hour
 *
 * Server-side rate limiting via Supabase Edge Functions is the real guard.
 * This client-side limiter is a first line of defense.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from './logger';

const log = createLogger('RATE_LIMITER');
const STORAGE_KEY = 'duel:rate_limits';

interface RateBucket {
  count: number;
  windowStart: number;
}

const LIMITS: Record<string, { maxPerHour: number }> = {
  workout_save: { maxPerHour: 10 },
  score_recompute: { maxPerHour: 20 },
  duel_create: { maxPerHour: 5 },
  duel_accept: { maxPerHour: 10 },
  profile_update: { maxPerHour: 30 },
  strength_event_insert: { maxPerHour: 50 },
  share_card_generate: { maxPerHour: 20 },
};

async function getBuckets(): Promise<Record<string, RateBucket>> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

async function saveBuckets(buckets: Record<string, RateBucket>): Promise<void> {
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(buckets));
}

/**
 * Check if an action is rate-limited. Returns true if allowed, false if blocked.
 */
export async function checkRateLimit(action: string): Promise<{ allowed: boolean; remaining: number; resetIn: number }> {
  const limit = LIMITS[action];
  if (!limit) return { allowed: true, remaining: 999, resetIn: 0 };

  const buckets = await getBuckets();
  const bucket = buckets[action];
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;

  if (!bucket || (now - bucket.windowStart) > oneHour) {
    // New window
    buckets[action] = { count: 1, windowStart: now };
    await saveBuckets(buckets);
    return { allowed: true, remaining: limit.maxPerHour - 1, resetIn: oneHour };
  }

  if (bucket.count >= limit.maxPerHour) {
    const resetIn = oneHour - (now - bucket.windowStart);
    log.warn(`Rate limit hit: ${action} (${bucket.count}/${limit.maxPerHour})`, { resetIn });
    return { allowed: false, remaining: 0, resetIn };
  }

  bucket.count++;
  await saveBuckets(buckets);
  return { allowed: true, remaining: limit.maxPerHour - bucket.count, resetIn: oneHour - (now - bucket.windowStart) };
}

/**
 * Record an action. Call after successful completion.
 */
export async function recordAction(action: string): Promise<void> {
  // Already incremented in checkRateLimit — this is for actions that check separately
  const result = await checkRateLimit(action);
  if (!result.allowed) {
    log.warn(`Rate limit post-record: ${action} blocked`);
  }
}

// --- Synchronous in-memory rate limiter (for callers that need sync checks) ---
const syncBuckets: Record<string, { count: number; windowStart: number }> = {};

/**
 * Synchronous rate limit check. Returns true if the action is rate-limited (blocked).
 * Usage: if (isRateLimited('follow', 10, 60_000)) return;
 */
export function isRateLimited(action: string, maxCount: number, windowMs: number): boolean {
  const now = Date.now();
  const bucket = syncBuckets[action];
  if (!bucket || (now - bucket.windowStart) > windowMs) {
    syncBuckets[action] = { count: 1, windowStart: now };
    return false;
  }
  if (bucket.count >= maxCount) {
    log.warn(`[RateLimit] ${action} blocked (${bucket.count}/${maxCount})`);
    return true;
  }
  bucket.count++;
  return false;
}

// Pending dedup map for deduplicatedFetch
const pendingFetches: Map<string, Promise<any>> = new Map();

/**
 * Deduplicated fetch — prevents concurrent identical requests.
 * If the same key is already in-flight, returns the existing promise.
 */
export async function deduplicatedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (pendingFetches.has(key)) {
    return pendingFetches.get(key) as Promise<T>;
  }
  const promise = fetcher().finally(() => pendingFetches.delete(key));
  pendingFetches.set(key, promise);
  return promise;
}
